/**
 * AbteilungenSettings — Admin Einstellungen → Abteilungen
 *
 * Liste der Abteilungen (Badges Personengruppe, Mitglieder, Stages, Rollen,
 * Aktiv), „Neue Abteilung" und Detailansicht mit Tabs (AbteilungDetail).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Icon, SectionTitle } from '../../../components/ui';
import { departmentsApi, loadMyDepartments, type Department, type RbacRole } from '../../../lib/departments';
import { getThemeColors } from '../../../lib/theme';
import { AbteilungNewForm } from './AbteilungNewForm';
import { AbteilungDetail } from './AbteilungDetail';
import { apiErrorMessage, groupKey } from './shared';
import type { Density } from '../../../types';

interface Props {
  dark: boolean;
  density?: Density;
  /** Springt zur Sektion „Rollen & Rechte". */
  onOpenRoles?: () => void;
}

type View = { mode: 'list' } | { mode: 'new' } | { mode: 'detail'; id: number };

export function AbteilungenSettings({ dark, density, onOpenRoles }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [view, setView] = useState<View>({ mode: 'list' });
  const [departments, setDepartments] = useState<Department[] | null>(null);
  const [roles, setRoles] = useState<RbacRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, allRoles] = await Promise.all([departmentsApi.list(), departmentsApi.roles().catch(() => [] as RbacRole[])]);
      setDepartments(list);
      setRoles(allRoles);
      setError(null);
    } catch {
      setError(tr('departments.settings.load_error'));
      setDepartments([]);
    }
  }, [tr]);

  useEffect(() => { void load(); }, [load]);

  /** Nach Änderungen: Liste und Menü (Sidebar) aktualisieren. */
  const changed = useCallback(async () => {
    await load();
    void loadMyDepartments(true);
  }, [load]);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await departmentsApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      await changed();
    } catch (e) {
      setError(apiErrorMessage(e, tr('departments.settings.delete_error')));
      setDeleteTarget(null);
    }
  };

  if (view.mode === 'new') {
    return (
      <AbteilungNewForm
        dark={dark}
        roles={roles}
        onCancel={() => setView({ mode: 'list' })}
        onCreated={async (d) => { await changed(); setView({ mode: 'detail', id: d.id }); }}
      />
    );
  }

  if (view.mode === 'detail') {
    return (
      <AbteilungDetail
        dark={dark}
        departmentId={view.id}
        density={density}
        roles={roles}
        onBack={() => { setView({ mode: 'list' }); void changed(); }}
        onChanged={() => void changed()}
        onOpenRoles={onOpenRoles}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionTitle dark={dark} title={tr('departments.settings.title')} subtitle={tr('departments.settings.subtitle')} />
      {error && <div role="alert" style={{ color: '#b42318', fontSize: 13 }}>{error}</div>}
      {departments == null && <div style={{ color: t.muted, fontSize: 13 }}>{tr('departments.page.loading')}</div>}
      {departments?.length === 0 && !error && (
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('departments.settings.empty')}</div>
      )}
      {departments?.map(d => {
        const roleNames = d.role_ids.map(id => roles.find(r => r.id === id)?.name).filter(Boolean) as string[];
        const protectedDept = d.pipeline !== 'custom';
        return (
          <Card key={d.id} dark={dark} style={{ padding: 16 }}>
            <div data-testid={`dept-row-${d.slug}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
              <div
                aria-hidden
                style={{
                  width: 40, height: 40, borderRadius: 10, background: d.color, color: '#fff', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                }}
              >
                {d.slug.slice(0, 3).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.heading }}>{d.name}</div>
                <div style={{ fontSize: 12, color: t.muted, margin: '2px 0 10px' }}>
                  {[d.description, tr('departments.settings.own_menu_block', { slug: d.slug })].filter(Boolean).join(' · ')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <Badge dark={dark} tone="brand">{tr(`departments.group.${groupKey(d.user_role)}_short`)}</Badge>
                  <Badge dark={dark}>{tr('departments.settings.members_count', { count: d.members_count })}</Badge>
                  <Badge dark={dark}>{tr('departments.settings.stages_count', { count: d.stages_count })}</Badge>
                  <Badge dark={dark} tone="info">
                    {roleNames.length > 0
                      ? tr('departments.settings.roles_badge', { count: roleNames.length, names: roleNames.join(', ') })
                      : tr('departments.settings.no_role')}
                  </Badge>
                  <Badge dark={dark} tone={d.is_active ? 'success' : 'warning'}>
                    {d.is_active ? tr('departments.settings.active') : tr('departments.settings.inactive')}
                  </Badge>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button dark={dark} variant="secondary" size="sm" onClick={() => setView({ mode: 'detail', id: d.id })}>
                  {tr('departments.settings.edit')}
                </Button>
                <span title={protectedDept ? tr('departments.settings.delete_protected') : undefined}>
                  <Button
                    dark={dark} variant="secondary" size="sm"
                    disabled={protectedDept}
                    onClick={() => setDeleteTarget(d)}
                  >
                    {tr('departments.settings.delete')}
                  </Button>
                </span>
              </div>
            </div>
          </Card>
        );
      })}
      <div>
        <Button dark={dark} variant="primary" icon={<Icon.Plus />} onClick={() => setView({ mode: 'new' })}>
          {tr('departments.settings.new')}
        </Button>
      </div>

      {deleteTarget && (
        <Card dark={dark} style={{ padding: 16, borderColor: '#fda29b' }}>
          <div role="alertdialog" aria-label={tr('departments.settings.delete_title')}>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 6 }}>
              {tr('departments.settings.delete_title', { name: deleteTarget.name })}
            </div>
            <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>{tr('departments.settings.delete_text')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button dark={dark} variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>{tr('departments.common.cancel')}</Button>
              <Button dark={dark} variant="primary" size="sm" onClick={() => void doDelete()}>{tr('departments.settings.delete_confirm')}</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
